import React, { useState, useEffect, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import moment from 'moment'
import ReactGA from 'react-ga'
import Swal from 'sweetalert2'
import { useForm } from 'react-hook-form'
import { registerLocale, CalendarContainer } from 'react-datepicker'
import ptBR from 'date-fns/locale/pt-BR'

import ExclamationCircle from '../../../assets/exclamation-circle.svg'
import { sendSolicitation } from '../../../services/solicitations'
import { getServices } from '../../../services/services'
import { getBreeds } from '../../../services/pet'
import { getAvailableDays } from '../../../services/scheduler'
import { getCampaignsByPartnerAndService } from '../../../services/campaigns'
import { FaChevronLeft } from 'react-icons/fa'
import { Container, CustomButton } from '../../../styles/global'
import { Input, Select, Radio, Textarea, DatePicker } from '../../../components/Form'
import CheckboxList from '../../../components/Form/CheckboxList'
import { SOLICITATION_CATEGORY, CAMPAIGN_CATEGORY_LABEL } from '../../../utils/constants'
import { formatCampaign } from '../../../utils/formatters'
import { PET_TEMPERAMENTS, PET_TYPES } from '@cobasi/cobasi-utils/constants'
import { getTemperamentLabel, getTypeLabel } from '@cobasi/cobasi-utils/helpers/pet'
import { RootContext } from '../../../context/rootContext'
import {
  Title,
  Row,
  Subtitle,
  RadioGroup,
  Label,
  CalendarCaptions,
  InfoBox
} from './styles'
import { serviceHelper } from '../../../utils/serviceHelper'
import { ReactComponent as PercentageIcon } from '../../../assets/icons/percent.svg'
import { ReactComponent as ClockIcon } from '../../../assets/icons/clock.svg'
import Alert from '../../../components/Form/Alert'
import { usePrevState } from '../../../hooks/usePrevState'

registerLocale('ptBR', ptBR)

const SchedulingSolicitationView = () => {
  const { register, handleSubmit, formState: { errors }, watch } = useForm()
  const [mainServicesList, setMainServicesList] = useState([])
  const [additionalServicesList, setAdditionalServicesList] = useState([])
  const [selectedAdditionalServicesList, setSelectedAdditionalServicesList] = useState([])
  const [breedList, setBreedList] = useState([])
  const [scheduleDatePicker, setScheduleDatePicker] = useState(moment().startOf('day').toDate())
  const { handleUpdateSolicitation, loadingState } = useContext(RootContext)
  const [loading, setLoading] = loadingState
  const [selectedCalcDate, setSelectedCalcDate] = useState(new Date())
  const [availableDates, setAvailableDates] = useState([])
  const [availableTimes, setAvailableTimes] = useState([])
  const [isOverbook, setIsOverbook] = useState(false)
  const watchService = watch('service')
  const watchPetBreed = watch('petBreed')
  const watchPetTemperament = watch('petTemperament')
  const [availabilityError, setAvailabilityError] = useState(false)
  const [campaigns, setCampaigns] = useState([])
  const [selectedCampaignByDate, setSelectedCampaignByDate] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const prevService = usePrevState(watchService)
  const prevCalcDate = usePrevState(selectedCalcDate)

  useEffect(() => {
    handleGetServices()
  }, [])

  useEffect(() => {
    setLoading(true)
    const promises = []
    if (watchService && watchPetBreed && watchPetTemperament) {
      const additionalServices = additionalServicesList.filter(service => selectedAdditionalServicesList.some(item => item.value === service.value)).map(service => service.value)
      promises.push(
        fetchAvailability({ service: watchService, additionalServices, breed: watchPetBreed, temperament: watchPetTemperament })
      )
    }
    if (prevService !== watchService || prevCalcDate !== selectedCalcDate) {
      const month = selectedCalcDate.getMonth() + 1
      const year = selectedCalcDate.getFullYear()
      const calendarDate = {
        month,
        year
      }
      promises.push(fetchCampaigns(calendarDate))
    }
    Promise.all(promises).finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchService, watchPetBreed, watchPetTemperament, selectedCalcDate, selectedAdditionalServicesList])

  useEffect(() => {
    if (scheduleDatePicker) {
      const unavailable = !availableTimes[scheduleDatePicker.getDate()] || !availableTimes[scheduleDatePicker.getDate()].includes(scheduleDatePicker.toString())
      setIsOverbook(unavailable)
      if (campaigns) {
        const campaign = campaigns.find(campaign => checkCampaignAvailability(scheduleDatePicker, campaign))
        setSelectedCampaignByDate(campaign)
      }
    }
  }, [scheduleDatePicker, availableTimes, campaigns])

  const fetchCampaigns = async (calendarDate) => {
    if (!watchService) return
    const { partner } = JSON.parse(localStorage.getItem('userData'))
    const service = watchService
    const campaignList = await getCampaignsByPartnerAndService(partner, service, calendarDate)
    setCampaigns(campaignList)
  }

  const checkCampaignAvailability = (date, campaign) => {
    const { start, end } = campaign
    const momentDate = moment(date)
    const momentStart = moment(start)
    const momentEnd = moment(end)

    return momentDate.isBetween(momentStart, momentEnd, null, '[]')
  }

  const handleChangeDatePicker = (value) => {
    if (value.getMonth() !== scheduleDatePicker.getMonth()) setSelectedCalcDate(value)
    setScheduleDatePicker(value)
    setShowAlert(true)
  }

  const generateClassNames = (date, isDate = true) => {
    const classNames = []
    if (isDate) {
      classNames.push(availableDates.includes(date.getDate().toString()) ? 'available' : 'unavailable')
    } else {
      const hours = date.getHours()
      classNames.push(availableTimes[date.getDate()] && availableTimes[date.getDate()].includes(date.toString()) ? 'available' : 'unavailable')
      classNames.push(hours < 7 || hours >= 22 ? ' d-none' : '')
    }
    if (campaigns) {
      campaigns.forEach(campaign => {
        if (checkCampaignAvailability(date, campaign)) {
          classNames.push('activeCampaign')
        }
      })
    }
    return classNames.join(' ')
  }

  const fetchAvailability = async ({ service, additionalServices, breed, temperament }) => {
    try {
      const { professional: { id: professionalId }, partner } = JSON.parse(localStorage.getItem('userData'))
      const month = selectedCalcDate.getMonth()
      const year = selectedCalcDate.getFullYear()
      const availability = await getAvailableDays({ partner, service, additionalServices, breed, temperament, month: month + 1, year })
      const dates = availability[professionalId] ? Object.keys(availability[professionalId]) : []

      // Converte os horários recebidos de String para Date
      Object.keys(availability).forEach(profId => {
        Object.keys(availability[profId]).forEach(day => {
          availability[profId][day] = availability[profId][day].map(
            time => {
              const [hours, minutes] = time.split(':').map(num => parseInt(num))
              return new Date(year, month, day, hours, minutes).toString()
            }
          )
        })
      })

      const times = availability[professionalId] || []
      setAvailableDates(dates)
      setAvailableTimes(times)
      setAvailabilityError(false)
    } catch (errors) {
      setAvailabilityError(true)
      const { partnerName, partnerPhone } = JSON.parse(localStorage.getItem('userData'))
      Swal.fire({
        title: '<h3 style=\'color: #222; font-size: 24px; font-weight: 700; font-family: Open Sans;\'>Ops, algumas opções não estão disponíveis</h3>',
        html: `
          <p style="color: #555; font-family: Open Sans; font-size: 14px; font-weight: 400; line-height: 20px;">Algumas das opções selecionadas não estão disponíveis para essa loja. Entre em contato para mais informações.</p>
          <div>
            <p style="color: #555; font-family: Open Sans; font-size: 14px; font-weight: 600; margin-bottom: 0;">Local: ${partnerName}</p>
            <p style="color: #555; font-family: Open Sans; font-size: 14px; font-weight: 600; margin-bottom: 0;">Telefone: ${partnerPhone}</p>
          </div>
        `,
        iconHtml: `<img src="${ExclamationCircle}" />`,
        showCancelButton: false,
        showConfirmButton: false,
        background: '#fff',
        showCloseButton: true,
        customClass: { closeButton: 'colorBlack', container: 'no-circle' }
      })
    }
  }

  const handleGetServices = async () => {
    const services = await getServices()
    const { mainServices, additionalServices } = serviceHelper.splitServiceAdditional(services)
    setMainServicesList(mainServices.map(({ id, name }) => ({ value: id, label: name })))
    setAdditionalServicesList(additionalServices.map(({ id, name }) => ({ value: id, label: name })))
  }

  const history = useHistory()

  const handleBackPage = () => {
    history.push('/solicitations')
    ReactGA.pageview('/solicitations')
  }

  const handleSendSolicitation = async form => {
    setLoading(true)
    const selectedBreed = breedList.find((breed) => breed.value === form.petBreed)
    const selectedMainService = mainServicesList.find((service) => service.value === form.service)
    const { professional } = JSON.parse(localStorage.getItem('userData'))

    const response = await sendSolicitation({
      professionalId: professional.id,
      kind: isOverbook
        ? SOLICITATION_CATEGORY.OVERBOOK
        : SOLICITATION_CATEGORY.SCHEDULING,
      solicitation: {
        date: scheduleDatePicker,
        userName: form.userName,
        service: {
          id: form.service,
          name: selectedMainService.label
        },
        additionalServices: selectedAdditionalServicesList.map(({ value }) => value),
        pet: {
          name: form.petName,
          castrated: (form.petCastrated === 'true'),
          temperament: form.petTemperament,
          breed: {
            id: form.petBreed,
            name: selectedBreed.label,
            type: form.petType
          }
        },
        obs: form.schedulingObs
      }
    })

    if (response.errors) {
      Swal.fire({
        title: '<h3 style=\'color: #000; font-size: 24px; font-weight: 700; font-family: "Nunito"\'>Erro!</h3>',
        html: '<p style="color: #000;">Não foi possível enviar sua solicitação, tente novamente</p>',
        icon: 'error',
        showCancelButton: false,
        showConfirmButton: false,
        background: '#fff',
        showCloseButton: true,
        customClass: { closeButton: 'colorBlack' }
      })
    } else {
      const newSolicitation = {
        ...response.data,
        solicitation: {
          ...response.data.solicitation,
          additionalServices: selectedAdditionalServicesList.map(({ value, label }) => ({ _id: value, name: label }))
        }
      }
      handleUpdateSolicitation(response.data.id, newSolicitation)
      Swal.fire({
        title: '<h3 style=\'color: #fff; font-size: 24px; font-weight: 400; font-family: "Nunito"\'>Solicitação <br><strong>enviada com<br> sucesso!</strong></h3>',
        text: '',
        icon: 'success',
        iconColor: '#fff',
        showCancelButton: false,
        showConfirmButton: false,
        background: '#009CA6',
        showCloseButton: true,
        customClass: { closeButton: 'colorWhite' }
      }).then((result) => {
        if (result.isDismissed) handleBackPage()
      })
    }

    setLoading(false)
  }

  const loadPetBreeds = async (petType) => {
    setLoading(true)
    const breedsResponse = (await getBreeds(petType)) || []
    const breedsListMapped = breedsResponse.map(({ id, name }) => ({ value: id, label: name }))
    setBreedList(breedsListMapped)
    setLoading(false)
  }

  const defaultError = 'Campo obrigatório'
  const temperamentsList = Object.values(PET_TEMPERAMENTS).map(temp => ({ value: temp, label: getTemperamentLabel(temp) }))
  const petTypeList = Object.values(PET_TYPES).map(type => ({ value: type, label: getTypeLabel(type) }))

  const CustomContainer = ({ className, children }) => {
    return (
      <div>
        <CalendarContainer className={className}>
          <div style={{ position: 'relative' }}>{children}</div>
          <div style={{ position: 'relative', clear: 'both' }}>
            <CalendarCaptions>
              <li><PercentageIcon src={PercentageIcon} />Campanha promocional</li>
              <li><ClockIcon src={ClockIcon} className='scheduled'/>Datas e/ou horários agendados</li>
            </CalendarCaptions>
          </div>
        </CalendarContainer>
      </div>
    )
  }

  return (
    <Container>
      <InfoBox>
        <Title>Novo agendamento ou encaixe</Title>
        <p>Preencha o formulário normalmente para um novo agendamento ou solicite um encaixe selecionando um horário já agendado.</p>
      </InfoBox>
      <form onSubmit={handleSubmit(handleSendSolicitation)} autoComplete='off'>
        <Subtitle>Tutor</Subtitle>
        <Row>
          <Input
            label='Nome completo (obrigatório)'
            type='text'
            name='userName'
            placeholder='Nome do dono'
            hookForm={register('userName', {
              required: true,
              minLength: 2
            })}
            errorMessage={errors.userName ? defaultError : ''}
          />
        </Row>

        <Subtitle>Pet</Subtitle>
        <Row>
          <Input
            label='Nome (obrigatório)'
            type='text'
            name='petName'
            placeholder='Nome do pet'
            hookForm={register('petName', {
              required: true
            })}
            errorMessage={errors.petName ? defaultError : ''}
          />
        </Row>
        <Row>
          <Select
            overrideStyles={{ height: '56px' }}
            label='Espécie (obrigatório)'
            options={petTypeList}
            name='petType'
            hookForm={register('petType', {
              required: true
            })}
            onChange={loadPetBreeds}
            errorMessage={errors.petType ? defaultError : ''}
          />
        </Row>
        <Row>
          <Select
            hasTextInput
            overrideStyles={{ height: '56px' }}
            label='Raça (obrigatório)'
            options={!loading ? breedList : [{ value: '', label: 'Carregando...' }]}
            name='petBreed'
            hookForm={register('petBreed', {
              required: true
            })}
            errorMessage={errors.petBreed ? defaultError : ''}
          />
        </Row>
        <Row>
          <Label>Temperamento (obrigatório)</Label>
          <RadioGroup>
            {temperamentsList.map(({ label, value }, index) => (
              <Radio
                key={value + '-' + index}
                label={label}
                name='petTemperament'
                hookForm={register('petTemperament', {
                  required: true
                })}
                value={value}
                errorMessage={errors.petTemperament ? defaultError : ''}
              />
            ))}
          </RadioGroup>
        </Row>
        <Row>
          <Label>Castrado (obrigatório)</Label>
          <RadioGroup>
            <Radio
              label='Não'
              name='petCastrated'
              hookForm={register('petCastrated', {
                required: true
              })}
              value={false}
              errorMessage={errors.petCastrated ? defaultError : ''}
            />
            <Radio
              label='Sim'
              name='petCastrated'
              hookForm={register('petCastrated', {
                required: true
              })}
              value={true}
              errorMessage={errors.petCastrated ? defaultError : ''}
            />
          </RadioGroup>
        </Row>

        <Subtitle>Serviço</Subtitle>
        <Row>
          <Select
            overrideStyles={{ height: '56px' }}
            label='Serviço principal (obrigatório)'
            options={mainServicesList}
            name='service'
            hookForm={register('service', {
              required: true
            })}
            errorMessage={errors.service ? defaultError : ''}
          />
        </Row>
        <Row>
          <Subtitle>Escolha serviços adicionais</Subtitle>
          <CheckboxList
            list={additionalServicesList}
            selectedList={[selectedAdditionalServicesList, setSelectedAdditionalServicesList]}
            selectAll={true}
            selectAllLabel={'Todos os serviços'}
            expanded={true}
          />
        </Row>
        <Row>
          <DatePicker
            label='Data do agendamento (obrigatório)'
            locale='ptBR'
            className='form-control'
            placeholderText='Data do agendamento'
            selected={scheduleDatePicker}
            dateFormat='dd/MM/yyyy HH:mm'
            onChange={value => handleChangeDatePicker(value)}
            showTimeSelect
            timeIntervals={15}
            disabledKeyboardNavigation
            required
            minDate={new Date()}
            dayClassName={(date) => generateClassNames(date)}
            timeClassName={(time) => {
              const hours = time.getHours()
              const minutes = time.getMinutes()
              const seconds = time.getSeconds()
              const selectedTime = moment(scheduleDatePicker || time).hours(hours).minutes(minutes).seconds(seconds)
              return generateClassNames(selectedTime.toDate(), false)
            }}
            calendarContainer={CustomContainer}
            onCalendarOpen={() => {
              if (!scheduleDatePicker) setScheduleDatePicker(moment().startOf('day').toDate())
              else if (selectedCalcDate.getMonth() !== scheduleDatePicker.getMonth()) {
                setSelectedCalcDate(scheduleDatePicker)
              }
            }}
            onMonthChange={date => {
              setSelectedCalcDate(date)
              setScheduleDatePicker(date)
              setSelectedCampaignByDate(false)
            }}
            disabled={!watchService || !watchPetBreed || !watchPetTemperament}
          />
        </Row>
        {isOverbook &&
          <Row className='alert-message'>
            <Alert
              type='info'
              show={showAlert}
            >
              Lembrando que <strong>já existe um agendamento no horário selecionado.</strong> Ao salvar, seu agendamento se tornará um encaixe.
            </Alert>
          </Row>
        }
        {selectedCampaignByDate &&
          <Row className='alert-message'>
            <Alert
              type='campaign'
              show={showAlert}
            >
              Existe uma campanha ativa na data selecionada. <strong>{CAMPAIGN_CATEGORY_LABEL[selectedCampaignByDate.category]}</strong> com <strong>{formatCampaign[selectedCampaignByDate.discountType](selectedCampaignByDate.discountValue)} de desconto</strong> no valor final do serviço.
            </Alert>
          </Row>
        }
        <Row>
          <Textarea
            label='Observações (opcional)'
            name='schedulingObs'
            placeholder='Escreva aqui alguma informação importante sobre o serviço ou pet. (Máximo 255 caracteres.)'
            hookForm={register('schedulingObs', {
              maxLength: 255
            })}
            errorMessage={errors.schedulingObs ? 'Limite máximo de 255 caracteres' : ''}
          />
        </Row>
        <Row>
          <CustomButton type='submit' disabled={loading || availabilityError}>Salvar e enviar {isOverbook ? 'encaixe' : 'agendamento'}</CustomButton>
          <CustomButton onClick={handleBackPage} outline>
            <FaChevronLeft color='#01878E' size='14' />
            Voltar
          </CustomButton>
        </Row>
      </form>
    </Container>
  )
}

export default SchedulingSolicitationView
